import React, { useEffect, useRef, useState } from 'react';
import { API_URL, apiFetch } from '../services/api';
import { hataMesaji } from '../utils/hataMesaji';

interface GoogleSignInButtonProps {
    onSuccess: (user: any, isNewUser: boolean) => void;
    onError?: (error: string) => void;
    role?: 'student' | 'consultant' | 'teacher';
    action?: 'login' | 'register';
    metadata?: any;
    buttonText?: string;
    className?: string;
}

declare global {
    interface Window {
        google?: {
            accounts: {
                id: {
                    initialize: (config: any) => void;
                    renderButton: (element: HTMLElement, config: any) => void;
                    prompt: () => void;
                };
            };
        };
    }
}

export const GoogleSignInButton: React.FC<GoogleSignInButtonProps> = ({
    onSuccess,
    onError,
    role = 'student',
    action = 'login',
    metadata = {},
    buttonText = 'Google ile Giriş Yap',
    className = ''
}) => {
    const buttonRef = useRef<HTMLDivElement>(null);
    const [isLoading, setIsLoading] = useState(false);
    const [scriptLoaded, setScriptLoaded] = useState(false);

    useEffect(() => {
        // Load Google Identity Services script
        if (!document.getElementById('google-identity-script')) {
            const script = document.createElement('script');
            script.id = 'google-identity-script';
            script.src = 'https://accounts.google.com/gsi/client';
            script.async = true;
            script.defer = true;
            script.onload = () => setScriptLoaded(true);
            document.body.appendChild(script);
        } else {
            setScriptLoaded(true);
        }
    }, []);

    useEffect(() => {
        if (!scriptLoaded || !buttonRef.current || !window.google) return;

        const clientId = '298601916346-uiit75104u644fhtib0mh6ihiqmn7ubn.apps.googleusercontent.com';

        window.google.accounts.id.initialize({
            client_id: clientId,
            callback: handleCredentialResponse
        });

        window.google.accounts.id.renderButton(buttonRef.current, {
            type: 'standard',
            theme: 'outline',
            size: 'large',
            text: 'signin_with',
            shape: 'rectangular',
            logo_alignment: 'left',
            width: 280
        });
    }, [scriptLoaded]);

    const handleCredentialResponse = async (response: any) => {
        setIsLoading(true);
        try {
            const result = await apiFetch(`${API_URL}/api/auth/google-signin`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    credential: response.credential,
                    role,
                    action,
                    metadata
                })
            });

            const data = await result.json();

            if (result.ok) {
                onSuccess(data.user, data.isNewUser);
            } else {
                onError?.(hataMesaji(data, 'Giriş başarısız'));
            }
        } catch (error) {
            console.error('Google Sign-In error:', error);
            onError?.('Bağlantı hatası');
        } finally {
            setIsLoading(false);
        }
    };

    if (isLoading) {
        return (
            <div className={`flex items-center justify-center gap-2 px-6 py-3 bg-white border-2 border-gray-200 rounded-xl ${className}`}>
                <div className="w-5 h-5 border-2 border-gray-300 border-t-blue-500 rounded-full animate-spin"></div>
                <span className="text-gray-600 font-medium">Giriş yapılıyor...</span>
            </div>
        );
    }

    return (
        <div className={`google-signin-wrapper ${className}`}>
            <div ref={buttonRef} className="google-signin-button"></div>
            <style>{`
                .google-signin-wrapper {
                    display: flex;
                    justify-content: center;
                }
                .google-signin-button {
                    border-radius: 12px;
                    overflow: hidden;
                }
                .google-signin-button > div {
                    border-radius: 12px !important;
                }
            `}</style>
        </div>
    );
};

// Alternative custom styled button
export const GoogleSignInButtonCustom: React.FC<GoogleSignInButtonProps> = ({
    onSuccess,
    onError,
    role = 'student',
    action = 'login',
    metadata = {},
    buttonText = 'Google ile Giriş Yap',
    className = ''
}) => {
    const [isLoading, setIsLoading] = useState(false);

    useEffect(() => {
        // Load Google Identity Services script
        if (!document.getElementById('google-identity-script')) {
            const script = document.createElement('script');
            script.id = 'google-identity-script';
            script.src = 'https://accounts.google.com/gsi/client';
            script.async = true;
            script.defer = true;
            document.body.appendChild(script);
        }
    }, []);

    const handleClick = () => {
        if (!window.google) {
            onError?.('Google servisleri yüklenemedi');
            return;
        }

        const clientId = '298601916346-uiit75104u644fhtib0mh6ihiqmn7ubn.apps.googleusercontent.com';

        window.google.accounts.id.initialize({
            client_id: clientId,
            callback: handleCredentialResponse
        });

        window.google.accounts.id.prompt();
    };

    const handleCredentialResponse = async (response: any) => {
        setIsLoading(true);
        try {
            const result = await apiFetch(`${API_URL}/api/auth/google-signin`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    credential: response.credential,
                    role,
                    action,
                    metadata
                })
            });

            const data = await result.json();

            if (result.ok) {
                onSuccess(data.user, data.isNewUser);
            } else {
                onError?.(hataMesaji(data, 'Giriş başarısız'));
            }
        } catch (error) {
            console.error('Google Sign-In error:', error);
            onError?.('Bağlantı hatası');
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <button
            onClick={handleClick}
            disabled={isLoading}
            className={`flex items-center justify-center gap-3 w-full px-6 py-3 bg-white border-2 border-gray-200 rounded-xl font-medium text-gray-700 hover:bg-gray-50 hover:border-gray-300 transition-all shadow-sm disabled:opacity-50 ${className}`}
        >
            {isLoading ? (
                <div className="w-5 h-5 border-2 border-gray-300 border-t-blue-500 rounded-full animate-spin"></div>
            ) : (
                <svg className="w-5 h-5" viewBox="0 0 24 24">
                    <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4" />
                    <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853" />
                    <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05" />
                    <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335" />
                </svg>
            )}
            {isLoading ? 'Giriş yapılıyor...' : buttonText}
        </button>
    );
};

export default GoogleSignInButton;
